import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { downloadCsv, downloadEntityPdf, downloadAuditBundle } from "@/lib/exports";
import {
  Wrench,
  FileText,
  Download,
  BellRing,
  Send,
  TrendingDown,
  Save,
  AlertTriangle,
  BookOpen,
  ChevronDown,
} from "lucide-react";

const fmt$ = (n) =>
  n == null ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

/**
 * Machinery service calls + Agreements summary.
 * Pulls /reports/machines/callout-response and /service-agreements so
 * managers see how their machinery service estate is tracking without
 * flipping between pages.
 */
const ServiceCallsAgreementsCard = () => {
  const [callouts, setCallouts] = useState(null);
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/reports/machines/callout-response", { params: { days: 90 } }).catch(() => ({ data: null })),
      api.get("/service-agreements").catch(() => ({ data: [] })),
    ])
      .then(([c, a]) => {
        setCallouts(c.data);
        setAgreements(a.data || []);
      })
      .finally(() => setLoading(false));
  }, []);

  const soon = useMemo(
    () =>
      (agreements || [])
        .filter((a) => {
          const lvl = a.expiry_status?.level;
          return lvl && lvl !== "ok" && lvl !== "unknown";
        })
        .sort(
          (a, b) =>
            (a.expiry_status?.days_remaining ?? 999) -
            (b.expiry_status?.days_remaining ?? 999),
        ),
    [agreements],
  );

  return (
    <div className="nb-card p-5" data-testid="reports-service-calls-card">
      <div className="flex items-center gap-2 mb-3">
        <Wrench size={16} strokeWidth={2.5} />
        <h2 className="font-display text-xl">Service calls & agreements</h2>
        <span className="text-xs text-[#607087] ml-auto">last 90 days</span>
      </div>
      {loading ? (
        <div className="text-sm text-[#607087]">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {/* Callouts snapshot */}
          <div
            className="border-2 border-black rounded-xl p-3 bg-[#EAF5FB]"
            data-testid="reports-callouts-summary"
          >
            <div className="label-eyebrow flex items-center gap-1.5">
              <AlertTriangle size={12} strokeWidth={2.5} /> Emergency call-outs
            </div>
            <div className="font-display text-4xl leading-none mt-1">
              {callouts?.incident_count ?? 0}
            </div>
            <div className="text-xs text-[#607087] mt-0.5">
              incidents · avg{" "}
              <b>
                {callouts?.avg_response_hours != null
                  ? `${callouts.avg_response_hours}h`
                  : "—"}
              </b>{" "}
              response
            </div>
            {callouts?.by_supplier?.length > 0 && (
              <div className="mt-2 text-[11px] font-mono space-y-0.5">
                {callouts.by_supplier.slice(0, 3).map((r) => (
                  <div
                    key={r.supplier_name}
                    className="flex justify-between gap-2"
                  >
                    <span className="truncate">{r.supplier_name}</span>
                    <span>
                      <b>{r.avg_hours}h</b>
                      <span className="text-[#607087]"> · {r.incidents}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Agreements summary */}
          <div
            className="border-2 border-black rounded-xl p-3 bg-[#FFF7D6]"
            data-testid="reports-agreements-summary"
          >
            <div className="label-eyebrow flex items-center gap-1.5">
              <FileText size={12} strokeWidth={2.5} /> Active agreements
            </div>
            <div className="font-display text-4xl leading-none mt-1">
              {agreements.length}
            </div>
            <div className="text-xs text-[#607087] mt-0.5">
              {soon.length > 0
                ? `${soon.length} need attention`
                : "all healthy"}
            </div>
            <div className="text-[11px] mt-2">
              Total covered:{" "}
              <b>
                {new Set(
                  agreements.flatMap((a) => a.covered_machine_ids || []),
                ).size}{" "}
                machines
              </b>
            </div>
          </div>

          {/* Renewal list */}
          <div
            className="border-2 border-black rounded-xl p-3 bg-white"
            data-testid="reports-agreements-renewals"
          >
            <div className="label-eyebrow flex items-center gap-1.5">
              <BellRing size={12} strokeWidth={2.5} /> Renewals in window
            </div>
            {soon.length === 0 && (
              <div className="text-xs text-[#607087] mt-2">
                No agreements in the renewal window — nothing to chase.
              </div>
            )}
            <ul className="space-y-1 mt-1 max-h-40 overflow-y-auto text-[11px]">
              {soon.slice(0, 8).map((a) => (
                <li
                  key={a.id}
                  className="flex items-center justify-between gap-2"
                >
                  <span className="truncate">{a.name}</span>
                  <span className="font-bold whitespace-nowrap">
                    {a.expiry_status?.days_remaining >= 0
                      ? `${a.expiry_status.days_remaining}d`
                      : `${Math.abs(a.expiry_status.days_remaining)}d overdue`}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Renewal reminder settings — set the single ops email + toggle scheduled
 * nudges. Also has a "Send now" button so managers can trigger a manual push.
 */
const RenewalReminderSettings = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = () =>
    api
      .get("/reminders/renewal-preview")
      .then(({ data }) => {
        setS(data.settings);
        setPreview(data.items);
      })
      .catch(() => toast.error("Could not load reminder settings"));

  useEffect(() => {
    load();
  }, []);

  if (!s) return null;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/reminders/renewal-settings", {
        enabled: s.enabled,
        ops_email: s.ops_email,
        lead_days: s.lead_days,
        cadence_days: s.cadence_days,
      });
      setS(data);
      toast.success("Reminder settings saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    if (!s.ops_email) {
      toast.error("Set an ops email first");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/reminders/renewal-send-now");
      if (data.sent) toast.success(`Sent ${data.count} reminder(s) to ${s.ops_email}`);
      else toast.info(data.reason || "Nothing to send");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="nb-card p-5"
      data-testid="reports-renewal-reminder-card"
    >
      <div className="flex items-center gap-2 mb-2">
        <BellRing size={16} strokeWidth={2.5} />
        <h2 className="font-display text-xl">Renewal reminders</h2>
      </div>
      <p className="text-xs text-[#607087] mb-3">
        Emails a single ops address <b>{s.lead_days}d</b> before any service
        agreement expires, then again every <b>{s.cadence_days}d</b> until
        renewed. {preview?.length ?? 0} agreement
        {preview?.length === 1 ? "" : "s"} in the current window.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="sm:col-span-2">
          <div className="label-eyebrow mb-1">Ops email</div>
          <input
            data-testid="renewal-ops-email"
            type="email"
            className="nb-input"
            value={s.ops_email}
            onChange={(e) => setS({ ...s, ops_email: e.target.value })}
            placeholder="ops@yourlab.com"
          />
        </label>
        <label>
          <div className="label-eyebrow mb-1">Lead days</div>
          <input
            data-testid="renewal-lead-days"
            type="number"
            min={1}
            max={180}
            className="nb-input"
            value={s.lead_days}
            onChange={(e) => setS({ ...s, lead_days: Number(e.target.value) })}
          />
        </label>
        <label>
          <div className="label-eyebrow mb-1">Repeat every (days)</div>
          <input
            data-testid="renewal-cadence-days"
            type="number"
            min={1}
            max={30}
            className="nb-input"
            value={s.cadence_days}
            onChange={(e) =>
              setS({ ...s, cadence_days: Number(e.target.value) })
            }
          />
        </label>
        <label className="sm:col-span-2 flex items-center gap-2 border-2 border-black rounded-lg px-3 py-2 bg-[#FFF9EC]">
          <input
            type="checkbox"
            data-testid="renewal-enabled"
            checked={!!s.enabled}
            onChange={(e) => setS({ ...s, enabled: e.target.checked })}
            className="w-4 h-4 accent-black"
          />
          <span className="text-sm">Enable scheduled reminders (daily check at 08:00)</span>
        </label>
      </div>
      <div className="flex items-center gap-2 mt-3 flex-wrap">
        <button
          data-testid="renewal-save-btn"
          onClick={save}
          disabled={saving}
          className="nb-btn nb-btn-primary px-4 py-2 flex items-center gap-2"
        >
          <Save size={14} strokeWidth={2.5} /> {saving ? "Saving…" : "Save"}
        </button>
        <button
          data-testid="renewal-send-now-btn"
          onClick={sendNow}
          disabled={sending || !s.ops_email}
          className="nb-btn bg-white px-4 py-2 flex items-center gap-2"
        >
          <Send size={14} strokeWidth={2.5} />{" "}
          {sending ? "Sending…" : "Send test now"}
        </button>
      </div>
    </div>
  );
};

/**
 * Turnover ratio — Consumption ÷ Spend per item. Slowest movers first.
 * Ratios < 1 = you spent more than you used (dead stock candidate).
 */
export const TurnoverCard = () => {
  const [days, setDays] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Defaults collapsed — the table can be dozens of rows and pushes the
  // more actionable Renewal + Exports cards below the fold otherwise.
  const [open, setOpen] = useState(false);

  const load = (d) => {
    setLoading(true);
    api
      .get("/reports/turnover", { params: { days: d } })
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Turnover load failed"))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load(days);
  }, [days]);

  const items = data?.items || [];

  return (
    <div className="nb-card p-5" data-testid="reports-turnover-card">
      <button
        type="button"
        data-testid="reports-turnover-toggle"
        onClick={() => setOpen((x) => !x)}
        aria-expanded={open}
        className="w-full flex items-center gap-2 flex-wrap text-left"
      >
        <TrendingDown size={16} strokeWidth={2.5} />
        <h2 className="font-display text-xl flex items-center gap-2">
          Turnover ratio
          {items.length > 0 && (
            <span className="text-[11px] font-bold text-white bg-black px-1.5 py-0.5 rounded">
              {items.length}
            </span>
          )}
        </h2>
        <span className="text-xs text-[#607087]">
          Consumption ÷ Spend · slowest movers first
        </span>
        <ChevronDown
          size={16}
          strokeWidth={2.5}
          className={`ml-auto transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="mt-3">
          <div className="flex gap-1 mb-3 justify-end">
            {[30, 90, 180, 365].map((d) => (
              <button
                key={d}
                data-testid={`turnover-days-${d}`}
                onClick={() => setDays(d)}
                className={`nb-btn px-2 py-1 text-xs ${
                  days === d ? "nb-btn-primary" : "bg-white"
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
          {loading ? (
            <div className="text-sm text-[#607087]">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-sm text-[#607087]">
              No movements in this window — nothing to rate.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                <div className="border-2 border-black rounded-lg px-3 py-2 bg-[#EAF5FB]">
                  <div className="label-eyebrow text-[10px]">Spend</div>
                  <div className="font-display text-2xl">
                    {fmt$(data.total_spend)}
                  </div>
                </div>
                <div className="border-2 border-black rounded-lg px-3 py-2 bg-[#FFF7D6]">
                  <div className="label-eyebrow text-[10px]">Consumption</div>
                  <div className="font-display text-2xl">
                    {fmt$(data.total_consumption)}
                  </div>
                </div>
                <div className="border-2 border-black rounded-lg px-3 py-2 bg-white col-span-2 sm:col-span-1">
                  <div className="label-eyebrow text-[10px]">Overall ratio</div>
                  <div className="font-display text-2xl">
                    {data.total_spend > 0
                      ? (data.total_consumption / data.total_spend).toFixed(2)
                      : "—"}
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[#607087] text-xs">
                      <th className="py-1.5">Item</th>
                      <th className="py-1.5">Dept</th>
                      <th className="py-1.5 text-right">Spend</th>
                      <th className="py-1.5 text-right">Consumed</th>
                      <th className="py-1.5 text-right">Ratio</th>
                    </tr>
                  </thead>
                  <tbody data-testid="turnover-rows">
                    {items.slice(0, 40).map((r) => {
                      const slow = r.ratio != null && r.ratio < 0.5;
                      const stale = r.ratio == null && r.spend > 0;
                      return (
                        <tr
                          key={r.stock_id}
                          data-testid={`turnover-row-${r.stock_id}`}
                          className={`border-t border-black/10 ${
                            slow || stale ? "bg-[#FCE8EA]" : ""
                          }`}
                        >
                          <td className="py-1.5 font-bold">{r.name}</td>
                          <td className="py-1.5 text-[#607087]">
                            {r.department || "—"}
                          </td>
                          <td className="py-1.5 text-right font-mono-num">
                            {fmt$(r.spend)}
                          </td>
                          <td className="py-1.5 text-right font-mono-num">
                            {fmt$(r.consumption_value)}
                          </td>
                          <td className="py-1.5 text-right font-bold">
                            {r.ratio == null ? "—" : r.ratio.toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="text-[10px] text-[#607087] mt-2">
                Red rows = ratio &lt; 0.5 (spent more than half went out) or spend
                without any consumption — good candidates to slow next order.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Downloads section — dedicated home for the 6-monthly audit exports.
 * Every row pulls its CSV from the backend, and its PDF from the canonical
 * `PDF_ENTITY_SPECS` in `lib/exports.js` — so the "Suppliers" PDF here is
 * byte-for-byte the same as the one on the /suppliers page.
 */
const ExportsCard = () => {
  const rows = [
    { entity: "stock", label: "Inventory · Consumables & materials", file: "inventory" },
    { entity: "suppliers", label: "Suppliers directory", file: "suppliers" },
    { entity: "machinery", label: "Machinery register", file: "machinery" },
    { entity: "assets", label: "Assets register", file: "assets" },
    { entity: "technicians", label: "Technicians directory", file: "technicians" },
    { entity: "service-agreements", label: "Service agreements", file: "service-agreements" },
    { entity: "site-services", label: "Site Services · Providers", file: "site-services" },
    { entity: "site-service-visits", label: "Site Services · Visits", file: "site-service-visits" },
  ];

  return (
    <div className="nb-card p-5" data-testid="reports-exports-card">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <Download size={16} strokeWidth={2.5} />
        <h2 className="font-display text-xl">Data exports</h2>
        <span className="text-xs text-[#607087]">
          for 6-monthly review by each department
        </span>
        <button
          data-testid="exports-audit-bundle-btn"
          onClick={downloadAuditBundle}
          className="ml-auto nb-btn nb-btn-primary px-3 py-1.5 text-xs font-bold flex items-center gap-1.5"
          title="One PDF · cover + every section · one page break per entity"
        >
          <BookOpen size={12} strokeWidth={2.5} />
          Print full audit bundle
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {rows.map((r) => (
          <div
            key={r.entity}
            data-testid={`exports-row-${r.entity}`}
            className="border-2 border-black rounded-lg p-3 flex items-center gap-3 bg-white"
          >
            <div className="flex-1 min-w-0">
              <div className="font-bold truncate">{r.label}</div>
              <div className="text-[10px] text-[#607087]">
                CSV = raw backend export · PDF = formatted table
              </div>
            </div>
            <button
              data-testid={`exports-${r.entity}-csv`}
              onClick={() => downloadCsv(r.entity, r.file)}
              className="nb-btn bg-white px-3 py-1.5 text-xs font-bold flex items-center gap-1"
            >
              <Download size={12} strokeWidth={2.5} /> CSV
            </button>
            <button
              data-testid={`exports-${r.entity}-pdf`}
              onClick={() => downloadEntityPdf(r.entity)}
              className="nb-btn bg-[#FFF7D6] px-3 py-1.5 text-xs font-bold flex items-center gap-1"
            >
              <FileText size={12} strokeWidth={2.5} /> PDF
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const ReportsExtrasSection = () => (
  <div className="grid grid-cols-1 gap-4 mt-5">
    <ServiceCallsAgreementsCard />
    <RenewalReminderSettings />
    <ExportsCard />
  </div>
);

export default ReportsExtrasSection;
